import type { UseMutationResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import type { TableUIPreferences } from "@/shared/domain/config/ConfigRecords";

import type { TablePreferences } from "./tablePreferencesConfig";

/**
 * Extract org-scoped preferences from config data for the current organization.
 *
 * @param configData - The full config data containing org_scoped_preferences
 * @param orgId - The current organization ID
 * @returns The preferences for the current org, or null if not found
 */
export function extractOrgPreferences(
  configData:
    | { org_scoped_preferences: Record<string, TableUIPreferences> }
    | undefined,
  orgId: string | undefined,
): TableUIPreferences | null {
  if (!orgId || !configData?.org_scoped_preferences) {
    return null;
  }
  return (
    (configData.org_scoped_preferences[orgId] as
      | TableUIPreferences
      | undefined) ?? null
  );
}

/**
 * Create preference object with defaults applied.
 *
 * @param orgPreferences - The org-specific preferences (may be null)
 * @param defaultColumns - Default columns for this table type
 * @param defaultRowsPerPage - Default rows per page
 * @returns Complete preferences object with defaults applied
 */
export function createPreferencesWithDefaults(
  orgPreferences: TableUIPreferences | null,
  defaultColumns: string[],
  defaultRowsPerPage: number,
): TablePreferences {
  return {
    columns: orgPreferences?.columns ?? defaultColumns,
    rowsPerPage: orgPreferences?.rows_per_page ?? defaultRowsPerPage,
  };
}

/**
 * Create callbacks for saving table preferences.
 *
 * @param updateMutation - The mutation hook for updating preferences
 * @param currentOrgId - The current organization ID
 * @returns Object containing saveColumns and saveRowsPerPage callbacks
 */
export function useCreatePreferenceCallbacks(
  updateMutation: UseMutationResult<
    unknown,
    Error,
    { orgId: string; preferences: Partial<TableUIPreferences> },
    unknown
  >,
  currentOrgId: string | undefined,
) {
  const saveColumns = useCallback(
    (columns: string[]) => {
      if (!currentOrgId) {
        return;
      }

      const updatedPrefs: Partial<TableUIPreferences> = {
        columns,
      };

      updateMutation.mutate({
        orgId: currentOrgId,
        preferences: updatedPrefs,
      });
    },
    [updateMutation, currentOrgId],
  );

  const saveRowsPerPage = useCallback(
    (rowsPerPage: number) => {
      if (!currentOrgId) {
        return;
      }

      const updatedPrefs: Partial<TableUIPreferences> = {
        rows_per_page: rowsPerPage,
      };

      updateMutation.mutate({
        orgId: currentOrgId,
        preferences: updatedPrefs,
      });
    },
    [updateMutation, currentOrgId],
  );

  const clearError = useCallback(() => {
    updateMutation.reset();
  }, [updateMutation]);

  return {
    saveColumns,
    saveRowsPerPage,
    clearError,
  };
}

/**
 * Hook to extract org preferences from config query data.
 * Memoized to prevent unnecessary re-renders.
 */
export function useOrgPreferences(
  configData:
    | { org_scoped_preferences: Record<string, TableUIPreferences> }
    | undefined,
  orgId: string | undefined,
): TableUIPreferences | null {
  return useMemo(
    () => extractOrgPreferences(configData, orgId),
    [configData, orgId],
  );
}
